import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { SITE, NAV_LINKS, SERVICES } from "../content";
import { openConsentBanner } from "../lib/analytics";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-ridge-charcoal text-white"
      data-testid="site-footer"
    >
      <div className="container-pad py-16 md:py-20 lg:py-24">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/logo-icon.png"
                alt=""
                aria-hidden="true"
                className="h-10 w-10 rounded bg-white p-0.5"
              />
              <span className="font-display text-sm font-bold uppercase tracking-[0.18em] text-white">
                {SITE.wordmark}
              </span>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/70">
              Premium courier and logistics services. {SITE.city}.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-md border border-white/15 px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-ridge-amber" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/85">
                {SITE.tagline}
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">
              Quick Links
            </h4>
            <span className="mt-3 block h-[2px] w-8 bg-ridge-amber" />
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.flatMap((link) => link.children ? link.children : [link]).map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/70 transition-colors hover:text-white"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/quote"
                  className="text-sm text-white/70 transition-colors hover:text-white"
                  data-testid="footer-link-quote"
                >
                  Get a Quote
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">
              Services
            </h4>
            <span className="mt-3 block h-[2px] w-8 bg-ridge-amber" />
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services#${s.slug}`}
                    className="text-sm text-white/70 transition-colors hover:text-white"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.short}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">
              Contact
            </h4>
            <span className="mt-3 block h-[2px] w-8 bg-ridge-amber" />
            <ul className="mt-5 space-y-4 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-ridge-amber" aria-hidden="true" />
                <Link
                  to="/contact"
                  className="transition-colors hover:text-white"
                  data-testid="footer-contact-phone"
                >
                  Contact via form
                </Link>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-ridge-amber" aria-hidden="true" />
                <a
                  href={`mailto:${SITE.email}`}
                  className="transition-colors hover:text-white"
                  data-testid="footer-contact-email"
                >
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ridge-amber" aria-hidden="true" />
                <span>{SITE.city}, Canada</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/55 md:flex-row md:items-center">
          <p data-testid="footer-copyright">
            © {year} Ridgehawk Logistics Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <Link
              to="/privacy"
              className="transition-colors hover:text-white"
              data-testid="footer-link-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="transition-colors hover:text-white"
              data-testid="footer-link-terms"
            >
              Terms of Service
            </Link>
            <button
              type="button"
              onClick={() => openConsentBanner()}
              className="transition-colors hover:text-white"
              data-testid="footer-link-cookies"
            >
              Manage cookies
            </button>
            <span className="font-semibold uppercase tracking-[0.18em]">
              {SITE.domain}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
